import { lazy, Suspense } from 'react';
const CommissionListPage = lazy(() => import('@apps/customer/pages/commission/Page'));
const FeeListPage = lazy(() => import('@apps/customer/pages/fee/Page'));
const PushToProductionListPage = lazy(() => import('@apps/customer/pages/push-to-production/Page'));
const SandboxListPage = lazy(() => import('@apps/customer/pages/sandbox/Page'));
const RoleMappingListPage = lazy(() => import('@apps/customer/pages/role-mapping/Page'));
const RolePublishListPage = lazy(() => import('@apps/customer/pages/role-publish/Page'));
const SystemUserApprovalListPage = lazy(
  async () => await import('../apps/customer/pages/system-user-approval/Page')
);
const SystemRolesListPage = lazy(
  async () => await import('../apps/customer/pages/system-roles/Page')
);
const SystemUsersListPage = lazy(
  async () => await import('../apps/customer/pages/system-users/Page')
);
const TaxSetupApprovalV2Page = lazy(
  async () => await import('../apps/customer/pages/tax-setup-approval/Page')
);
const TaxSetupV2Page = lazy(
  async () => await import('../apps/customer/pages/tax-setup/Page')
);
const TaxPeriodListPage = lazy(
  async () => await import('../apps/customer/pages/tax-period/Page')
);
const CompanyMappingListPage = lazy(() => import('@apps/customer/pages/company-mapping/Page'));
const FeatureSetListPage = lazy(() => import('@apps/customer/pages/feature-set/Page'));
const AccessProfileListPage = lazy(
  () => import('@apps/customer/pages/access-profiles/Page'),
);
const BankAccountListPage = lazy(() => import('@apps/customer/pages/bank-account/Page'));
const EmailTemplatesListPage = lazy(() => import('@apps/customer/pages/email-templates/Page'));
const CompanySsoListPage = lazy(() => import('@apps/customer/pages/company-sso/Page'));
import OptionAwardsApproval from '../pages/option-awards/optionsAwardsApproval';
import ParticipantsApproval from '../pages/participants/participantApproval';
import ApprovalConfig from '../pages/settings/approval-config';
import CompanyProfile from '../pages/settings/company-profile';
import CustomerSwitch from '../pages/settings/customer-switch';
import DepartmentComponent from '../pages/settings/department';
import EmployeeTypesComponent from '../pages/settings/employee-types';
import FeatureSet from '../pages/settings/feature-set';
import FinchComponent from '../pages/settings/finch';
import GrantManagementList from '../pages/settings/grant-management/grantList';
import JobTitleComponent from '../pages/settings/job-title';
import ParticipantBroadcast from '../pages/settings/participant-boadcast';

const PerformanceRulesListPage = lazy(
  () => import('../apps/customer/pages/performance-rules/Page')
);
const SharePoolManagementListPage = lazy(
  () => import('../apps/customer/pages/share-pool-management/Page')
);
const PerformanceRuleApprovalListPage = lazy(
  () => import('../apps/customer/pages/performance-rule-approval/Page')
);
const CustomerSuccessAutomationPage = lazy(
  () => import('../apps/customer/pages/customer-success-automation/Page')
);
import PptAccessListPage from '../pages/settings/ppt-access-list';
const ParticipantServicePage = lazy(
  () => import('../apps/customer/pages/participant-service/Page'),
);
import PerformanceRules from '../pages/settings/performance-rule';
import PerformanceRuleApproval from '../pages/settings/performance-rule/performanceRuleApproval';
import PlanManagement from '../pages/settings/plan-management';
import PushToProduction from '../pages/settings/push-to-production';
import RoleMapping from '../pages/settings/role-mapping';
import RolePublish from '../pages/settings/role-publish';
import SandboxSwitch from '../pages/settings/sandbox';
import SettlementConfig from '../pages/settings/settlement-config';
import SystemRoles from '../pages/settings/system-roles';
import SystemUsers from '../pages/settings/system-users';
import { INSTANCE_TENANT_SCOPE } from './tenantScope';
import TaxLibrary from '../pages/settings/tax/tax-library';
import TaxSetup from '../pages/settings/tax/tax-setup';
import TermAndCondition from '../pages/settings/term-and-condition';
import TradePreClearanceList from '../pages/settings/trade-pre-clearance';
import TradingBlackoutConfiguration from '../pages/settings/trading-black-out-configuration';
import Fee from '../pages/settings/fee';
import FmvConfiguration from '../pages/settings/fmv-configuration';
import Commission from '../pages/settings/commission';
const TransactionConfigPage = lazy(
  () => import('../apps/customer/pages/transaction-config/Page'),
);
import CompanyTnC from '../pages/settings/term-and-condition/companyTnC';
import TransferAgentSetting from '../pages/settings/transfer-agent-setting';
import AllInOneImport from '../pages/all-in-one-import';
import WhiteLabelConfiguration from '../pages/settings/white-label-configuration';
import { Ingest } from '../components/ingest';
import { PlatformKB } from '../components/platform-kb';
import PermissionManagement from '../pages/settings/permission-management';
import SearchPermissionOnUse from '../pages/settings/permission-management/searchPermissionOnUse';
import DisbursementConfig from '../pages/settings/disbursement-config';
import IsoConfiguration from '../pages/settings/iso-configuration';
import CompanyAccountingPolicy from '../pages/settings/company-accounting-policy';
import CharterStockClassesPage from '../apps/customer/pages/charter-stock-classes/Page';
// TODO: [sarv2-cleanup] removed - replaced by exercisability-instructions
// import ExerciseWindowConfig from '@apps/customer/pages/exercise-window-config/Page';
// import VintageConfigPage from '../apps/customer/pages/vintage-config/Page';
import ExercisabilityTierConfigPage from '../apps/customer/pages/exercisability-tier-config/Page';
import ExercisabilityInstructionsPage from '../apps/customer/pages/exercisability-instructions/Page';
import BrokerageSSOConfiguration from '../pages/settings/brokerage-sso-configuration';
import AdvisorApiConfigPage from '../pages/vendor_customer/advisor-api-config/Page';
import BrokerTradeTool from '../pages/settings/broker-trade-tool';
import DoneTrade from '../pages/settings/done-trade';
import MoneyMovementConfig from '../pages/settings/money-movement-config';
import SystemAlertConfiguration from '../pages/settings/system-alert-configuration';
import InvalidPasswordResetAnalyticsPage from '../apps/customer/pages/invalid-password-reset-analytics/Page';

const UserDefinedFieldsListPage = lazy(
  () => import('../apps/customer/pages/user-defined-fields/Page')
);
const RetirementEligibilityConfigPage = lazy(
  () => import('../apps/customer/pages/retirement-eligibility-config/Page'),
);
const AlertSubscriptionConfigPage = lazy(
  () => import('../apps/customer/pages/alert-subscription-config/Page'),
);
const EmailLogsListPage = lazy(() => import('../apps/customer/pages/email-logs/Page'));
const VendorTermsConditionsListPage = lazy(
  () => import('../apps/customer/pages/vendor-terms-conditions/Page')
);
const VestingTemplatesListPage = lazy(
  () => import('../apps/customer/pages/vesting-templates/Page')
);
const ParticipantApprovalListPage = lazy(
  () => import('../apps/customer/pages/participant-approval/Page')
);
const ParticipantBroadcastListPage = lazy(
  () => import('../apps/customer/pages/participant-broadcast/Page')
);
const GrantManagementListPage = lazy(() => import('../apps/customer/pages/grant-management/Page'));
const GrantAcceptanceTemplateListPage = lazy(
  () => import('../apps/customer/pages/grant-acceptance-template/Page')
);
const PlanApprovalListPage = lazy(() => import('../apps/customer/pages/plan-approval/Page'));
const OptionAwardApprovalListPage = lazy(
  () => import('../apps/customer/pages/option-award-approval/Page')
);
const TradePreClearanceListPage = lazy(
  () => import('../apps/customer/pages/trade-pre-clearance/Page')
);
const TradingBlackoutConfigListPage = lazy(
  () => import('../apps/customer/pages/trading-blackout-config/Page')
);
const TradePreClearanceApprovalListPage = lazy(
  () => import('../apps/customer/pages/trade-pre-clearance-approval/Page')
);
const BrokerageSsoConfigurationV2Page = lazy(
  async () => await import('../pages/settings/brokerage-sso-configuration/index')
);
const DepartmentsListPage = lazy(async () => await import('../apps/customer/pages/departments/Page'));
const JobTitlesListPage = lazy(async () => await import('../apps/customer/pages/job-titles/Page'));
const EmployeeTypesListPage = lazy(
  async () => await import('../apps/customer/pages/employee-types/Page')
);
const RsuAwardApprovalListPage = lazy(
  () => import('../apps/customer/pages/rsu-award-approval/Page')
);
const PsuAwardApprovalListPage = lazy(
  () => import('../apps/customer/pages/psu-award-approval/Page')
);
import BrokerAttestationAuditLogs from '../pages/settings/broker-attestation-audit-logs';

export enum Level {
  Company = 'company',
  Instance = 'instance',
}

export const OrganizationSettingsList = [
  {
    showOnList: true,
    url: 'company-profile',
    slug: 'company-profile',
    title: 'Company Profile',
    name: 'Companies',
    category: 'My Account',
    level: Level.Company,
    component: <CompanyProfile />,
  },
  {
    showOnList: true,
    url: 'users',
    slug: 'users',
    title: 'System Users',
    name: 'CustomerUsers',
    category: 'System Users & Roles',
    level: Level.Company,
    component: (
      <Suspense fallback={null}>
        <SystemUsersListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'roles',
    slug: 'roles',
    title: 'System Roles',
    name: 'CustomerRoles',
    category: 'System Users & Roles',
    level: Level.Company,
    component: (
      <Suspense fallback={null}>
        <AccessProfileListPage scope='Role' />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'system-approvers',
    slug: 'system-approvers',
    title: 'System Approvers',
    name: 'SystemApprovers',
    category: 'System Users & Roles',
    level: Level.Company,
    component: <ApprovalConfig />,
  },
  // {
  //   showOnList: true,
  //   url: 'users',
  //   slug: 'users',
  //   title: 'System Users',
  //   name: 'VendorUsers',
  //   category: 'System Users & Roles',
  //   component: <SystemUsers />,
  // },
  // {
  //   showOnList: true,
  //   url: 'roles',
  //   slug: 'roles',
  //   title: 'System Roles',
  //   name: 'VendorRoles',
  //   category: 'System Users & Roles',
  //   component: <SystemRoles />,
  // },
  // {
  //   showOnList: true,
  //   url: 'customer-success',
  //   slug: 'customer-success',
  //   title: 'Switch Company',
  //   name: 'SwitchCompany',
  //   category: 'Customer Success',
  //   component: <CustomerSwitch />,
  // },
  {
    showOnList: true,
    url: 'role-publish',
    slug: 'role-publish',
    title: 'Role Publish',
    name: 'RolePublishedToCompany',
    category: 'Customer Success',
    level: Level.Company,
    component: <Suspense fallback={null}>
      <RolePublishListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'role-mapping',
    slug: 'role-mapping',
    title: 'Role Mapping',
    name: 'AllowedRoleMapping',
    category: 'Customer Success',
    level: Level.Company,
    component: <Suspense fallback={null}>
      <RoleMappingListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'company-mapping',
    slug: 'company-mapping',
    title: 'Company Mapping',
    name: 'AllowedCompanyMapping',
    category: 'Customer Success',
    level: Level.Company,
    component: (
      <Suspense fallback={null}>
        <CompanyMappingListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'customer-success-automation',
    slug: 'customer-success-automation',
    title: 'Customer Success Automation',
    name: 'CustomerSuccess',
    category: 'Customer Success',
    level: Level.Company,
    component: (
      <Suspense fallback={null}>
        <CustomerSuccessAutomationPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'broker-trade-tool',
    slug: 'broker-trade-tool',
    title: 'Broker Trade Tool',
    name: 'BrokerTradeTool',
    category: 'Customer Success',
    level: Level.Instance,
    component: <BrokerTradeTool />,
  },
  {
    showOnList: true,
    url: 'done-trade',
    slug: 'done-trade',
    title: 'Done Trade',
    // Accept either dedicated DoneTrade permission or BrokerTradeTool until backend adds DoneTrade.
    name: ['DoneTrade', 'BrokerTradeTool'],
    category: 'Customer Success',
    level: Level.Instance,
    component: <DoneTrade />,
  },
  {
    showOnList: true,
    url: 'participant-service',
    slug: 'participant-service',
    title: 'Participant Service',
    permissionName: 'ParticipantService',
    name: 'ParticipantService',
    category: 'Customer Success',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <ParticipantServicePage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'sandbox',
    slug: 'sandbox',
    title: 'Sandbox',
    name: 'Sandbox',
    category: 'Sandbox',
    level: Level.Company,
    component: <Suspense fallback={null}>
      <SandboxListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'push-to-production',
    slug: 'push-to-production',
    title: 'Load To Main Logs',
    name: 'Production',
    category: 'Sandbox',
    level: Level.Company,
    component: <Suspense fallback={null}>
      <PushToProductionListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'feature-set',
    slug: 'feature-set',
    title: 'Feature Set',
    name: 'FeatureSet',
    category: 'Feature Set',
    level: Level.Company,
    component: (
      <Suspense fallback={null}>
        <AccessProfileListPage scope='FeatureSet' />
      </Suspense>
    ),
  },
  {
    showOnList: false,
    url: 'permission-manager',
    slug: 'permission-manager',
    title: 'Permission Manager',
    name: 'FeatureSet',
    category: 'Permission Manager',
    level: Level.Company,
    component: <PermissionManagement />,
  },
  {
    showOnList: false,
    url: 'search-permission-on-use',
    slug: 'search-permission-on-use',
    title: 'Permission On Use',
    name: 'FeatureSet',
    category: 'Permission Manager',
    level: Level.Company,
    component: <SearchPermissionOnUse />,
  },
  {
    showOnList: true,
    url: 'users/instance',
    slug: 'users/instance',
    title: 'Instance Users',
    name: 'CustomerUsers',
    category: 'Instance Users and Roles',
    level: Level.Instance,
    component: <SystemUsers scope={INSTANCE_TENANT_SCOPE} />,
  },
  {
    showOnList: true,
    url: 'roles/instance',
    slug: 'roles/instance',
    title: 'Instance Roles',
    name: 'CustomerRoles',
    category: 'Instance Users and Roles',
    level: Level.Instance,
    component: <SystemRoles scope={INSTANCE_TENANT_SCOPE} />,
  },
  {
    showOnList: true,
    url: 'department',
    slug: 'department',
    title: 'Departments',
    name: 'CustomerDepartments',
    category: 'Participant Settings',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <DepartmentsListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'job-title',
    slug: 'job-title',
    title: 'Job Titles',
    name: 'CustomerTitles',
    category: 'Participant Settings',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <JobTitlesListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'employee-types',
    slug: 'employee-types',
    title: 'Employee Types',
    name: 'CustomerEmployeeTypes',
    category: 'Participant Settings',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <EmployeeTypesListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'plan-management',
    slug: 'plan-management',
    title: 'Plan Management',
    name: 'Plans',
    category: 'Award Management',
    level: Level.Instance,
    component: <PlanManagement />,
  },
  {
    showOnList: true,
    url: 'grant-management',
    slug: 'grant-management',
    title: 'Grant Management',
    name: 'Plans',
    category: 'Award Management',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <GrantManagementListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'sharepool-management',
    slug: 'sharepool-management',
    title: 'Share Pool Management',
    name: 'Plans',
    category: 'Award Management',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <SharePoolManagementListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'vesting-templates',
    slug: 'vesting-templates',
    title: 'Vesting Templates',
    name: 'VestingTemplates',
    category: 'Award Management',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <VestingTemplatesListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'performance-rule',
    slug: 'performance-rule',
    title: 'Performance Rules',
    name: 'PerformanceRule',
    category: 'Award Management',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <PerformanceRulesListPage />
      </Suspense>
    ),
  },

  {
    showOnList: true,
    url: 'charter-stock-classes',
    slug: 'charter-stock-classes',
    title: 'Charter & Stock Classes',
    name: 'StockClass',
    category: 'Cap Table Management',
    level: Level.Instance,
    component: <CharterStockClassesPage />,
  },
  {
    showOnList: true,
    url: 'tax-period',
    slug: 'tax-period',
    title: 'Tax Period',
    name: 'CompanyTax',
    category: 'Tax',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TaxPeriodListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'tax-setup',
    slug: 'tax-setup',
    title: 'Tax Setup',
    name: 'CompanyTax',
    category: 'Tax',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TaxSetupV2Page />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'tax-library',
    slug: 'tax-library',
    title: 'Tax Library',
    name: 'CompanyTax',
    category: 'Tax',
    level: Level.Instance,
    component: <TaxLibrary />,
  },
  {
    showOnList: true,
    url: 'finch',
    slug: 'finch',
    title: 'HR Payroll Integration',
    name: 'HRIntergration',
    category: 'Configuration',
    level: Level.Instance,
    component: <FinchComponent />,
  },
  {
    showOnList: true,
    url: 'sso-configuration',
    slug: 'sso-configuration',
    title: 'SSO Configuration',
    name: 'SsoConfiguration',
    category: 'Configuration',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <CompanySsoListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'vendor-broker-terms-and-conditions',
    slug: 'vendor-broker-terms-and-conditions',
    title: 'Vendor & Broker T&C',
    name: 'VendorTermsAndConditions',
    category: 'T&Cs, Regulatory & Other Legal Documents',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <VendorTermsConditionsListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'company-terms-and-conditions',
    slug: 'company-terms-and-conditions',
    title: 'Company T&C',
    name: 'TermsAndConditions',
    category: 'T&Cs, Regulatory & Other Legal Documents',
    level: Level.Instance,
    component: <CompanyTnC />,
  },
  {
    showOnList: true,
    url: 'broker-attestation-audit-logs',
    slug: 'broker-attestation-audit-logs',
    title: 'Attestation Audit Logs',
    name: 'BrokerAttestationAuditLogs',
    category: 'T&Cs, Regulatory & Other Legal Documents',
    level: Level.Instance,
    component: <BrokerAttestationAuditLogs />,
  },
  // {
  //   showOnList: true,
  //   url: 'terms-and-conditions',
  //   slug: 'terms-and-conditions',
  //   title: 'T&Cs, Regulatory & Other Legal Documents',
  //   name: 'TermsAndConditions',
  //   category: 'Others',
  //   level: Level.Instance,
  //   component: <TermAndCondition />,
  // },
  {
    showOnList: true,
    url: 'trade-pre-clearance',
    slug: 'trade-pre-clearance',
    title: 'Trade Pre-Clearance',
    name: 'TradePreClearance',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TradePreClearanceListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'trading-black-out-configuration',
    slug: 'trading-black-out-configuration',
    title: 'Trading Black-out Configuration',
    name: 'TradingBlackOut',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TradingBlackoutConfigListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'bank-account',
    slug: 'bank-account',
    title: 'Bank Account',
    name: 'Banks',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <BankAccountListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'udf',
    slug: 'udf',
    title: 'UDF',
    name: 'UDF',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <UserDefinedFieldsListPage />
      </Suspense>
    ),
  },
  {
    showOnList: false,
    url: 'tax-management',
    slug: 'tax-management',
    title: 'Tax Management',
    name: 'CustomerTax',
    category: 'Others',
    level: Level.Instance,
    component: <h1>Bank Account</h1>,
  },
  {
    showOnList: true,
    url: 'all-in-one-import',
    slug: 'all-in-one-import',
    title: 'All In One Import',
    name: 'Companies',
    category: 'Others',
    level: Level.Instance,
    component: <AllInOneImport />,
    exact: true,
  },
  {
    showOnList: true,
    url: 'blackout-config',
    slug: 'blackout-config',
    title: 'Blackout Config',
    name: 'BlackoutConfigs',
    category: 'Award Management',
    level: Level.Instance,
    component: <h1>Bank Account</h1>,
  },
  {
    showOnList: true,
    url: 'settlement-config',
    slug: 'settlement-config',
    title: 'Settlement Configuration',
    name: [
      'SettlementConfigs',
      'ElectionWindowConfigurations',
      'TradePreClearanceTransactionPeriodConfigs',
    ],
    category: 'Others',
    level: Level.Instance,
    component: <SettlementConfig />,
  },
  {
    showOnList: true,
    url: 'retirement-config',
    slug: 'retirement-config',
    title: 'Retirement Configuration',
    name: 'RetirementEligibilityConfigs',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <RetirementEligibilityConfigPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'exercisability-instructions',
    slug: 'exercisability-instructions',
    title: 'Exercise Window Configuration',
    name: 'ExerciseWindowConfiguration',
    category: 'Others',
    level: Level.Instance,
    component: <ExercisabilityInstructionsPage />,
  },
  {
    showOnList: true,
    url: 'exercisability-tier-configuration',
    slug: 'exercisability-tier-configuration',
    title: 'Maturity Schedule Configuration',
    name: 'MaturitySchedule',
    category: 'Others',
    level: Level.Instance,
    component: <ExercisabilityTierConfigPage />,
  },
  // {
  //   showOnList: true,
  //   url: 'system-approvers',
  //   slug: 'system-approvers',
  //   title: 'System Approvers',
  //   name: 'VendorApprovers',
  //   category: 'System Users & Roles',
  //   component: <ApprovalConfig />,
  // },
  {
    showOnList: false,
    url: 'grant-management/:id',
    slug: 'grant-management',
    title: 'Grant Management',
    name: 'Plans',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <GrantManagementListPage />
      </Suspense>
    ),
  },
  {
    showOnList: false,
    url: 'grant-list',
    slug: 'grant-list',
    title: 'Grant list',
    name: 'Plans',
    category: 'Others',
    level: Level.Instance,
    component: <GrantManagementList />,
  },
  {
    showOnList: true,
    url: 'user-defined-field',
    slug: 'user-defined-field',
    title: 'User Defined Fields',
    name: 'UserDefinedFields',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <UserDefinedFieldsListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'participant-broadcast',
    slug: 'participant-broadcast',
    title: 'Participant Broadcast',
    name: 'ParticipantBroadcastMessages',
    category: 'Participant Settings',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <ParticipantBroadcastListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'ppt-access-list',
    slug: 'ppt-access-list',
    title: 'Advisor PPT Access',
    permissionName: 'ManagePptAccessList',
    name: 'ManagePptAccessList',
    category: 'Participant Settings',
    level: Level.Instance,
    component: <PptAccessListPage />,
  },

  {
    showOnList: true,
    url: 'email-logs',
    slug: 'email-logs',
    title: 'Email Logs',
    name: 'Notification',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <EmailLogsListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'email-template',
    slug: 'email-template',
    title: 'Email Template',
    name: 'NotificationTemplate',
    category: 'Others',
    component: (
      <Suspense fallback={null}>
        <EmailTemplatesListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'broker-sso-configuration',
    slug: 'broker-sso-configuration',
    title: 'Broker SSO Configuration',
    name: 'BrokerSsoConfiguration',
    category: 'Others',
    component: (
      <Suspense fallback={null}>
        <BrokerageSsoConfigurationV2Page />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'advisor-api-config',
    slug: 'advisor-api-config',
    title: 'Advisor API Configuration',
    name: 'ManageAdvisorApiKey',
    category: 'Others',
    component: <AdvisorApiConfigPage />,
  },
  {
    showOnList: true,
    url: 'alert-subscription',
    slug: 'alert-subscription',
    title: 'Alert Subscription',
    name: 'ManageAlertSubscription',
    category: 'Others',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <AlertSubscriptionConfigPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'grant-acceptance-template',
    slug: 'grant-acceptance-template',
    title: 'Grant Acceptance Template',
    name: 'Notice',
    category: 'Award Management',
    component: (
      <Suspense fallback={null}>
        <GrantAcceptanceTemplateListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/user-approval',
    slug: 'task/user-approval',
    permissionName: 'CustomerUserApproval',
    title: 'System User Approval',
    name: 'CustomerUserApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <SystemUserApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/plan-approval',
    slug: 'task/plan-approval',
    permissionName: 'PlanApproval',
    title: 'Plan Approval',
    name: 'PlanApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <PlanApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/participants-approval',
    slug: 'task/participants-approval',
    permissionName: 'ParticipantApproval',
    title: 'Participant Approval',
    name: 'ParticipantApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <ParticipantApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/rsu-awards-approval',
    slug: 'task/rsu-awards-approval',
    permissionName: 'RSUAwardApproval',
    title: 'RSU Awards Approval',
    name: 'RSUAwardApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <RsuAwardApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/psu-awards-approval',
    slug: 'task/psu-awards-approval',
    permissionName: 'PSUAwardApproval',
    title: 'PSU Awards Approval',
    name: 'PSUAwardApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <PsuAwardApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/option-awards-approval',
    slug: 'task/option-awards-approval',
    permissionName: 'OptionAwardApproval',
    title: 'Option Awards Approval',
    name: 'OptionAwardApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <OptionAwardApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/pre-clearance-requests',
    slug: 'task/pre-clearance-requests',
    permissionName: 'TradePreClearanceApproval',
    title: 'Trade Pre-Clearance Approval',
    name: 'TradePreClearanceApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TradePreClearanceApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/tax-setup-approval',
    slug: 'task/tax-setup-approval',
    permissionName: 'TaxApproval',
    title: 'Tax Approval',
    name: 'TaxApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TaxSetupApprovalV2Page />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'task/performance-rule-approval',
    slug: 'task/performance-rule-approval',
    permissionName: 'PerformanceRuleApproval',
    title: 'Performance Rule Approval',
    name: 'PerformanceRuleApproval',
    category: 'Approval Console',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <PerformanceRuleApprovalListPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'fee',
    slug: 'fee',
    title: 'Fee',
    name: 'Fee',
    category: 'Configuration',
    level: Level.Instance,
    component: <Suspense fallback={null}>
      <FeeListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'commission',
    slug: 'commission',
    title: 'Commission',
    name: 'Commission',
    category: 'Configuration',
    level: Level.Instance,
    component: <Suspense fallback={null}>
      <CommissionListPage />
    </Suspense>,
  },
  {
    showOnList: true,
    url: 'fmv-configuration',
    slug: 'fmv-configuration',
    title: 'FMV Configuration',
    name: 'FMVConfiguration',
    category: 'Configuration',
    level: Level.Instance,
    component: <FmvConfiguration />,
  },
  {
    showOnList: true,
    url: 'transaction-configuration',
    slug: 'transaction-configuration',
    title: 'Transaction Configuration',
    name: 'Plans',
    // name: 'TransactionConfiguration',
    category: 'Configuration',
    level: Level.Instance,
    component: (
      <Suspense fallback={null}>
        <TransactionConfigPage />
      </Suspense>
    ),
  },
  {
    showOnList: true,
    url: 'chatbot-ingest',
    slug: 'chatbot-ingest',
    title: 'AI Knowledge',
    name: 'ChatBotDocument',
    category: 'Configuration',
    level: Level.Instance,
    component: <Ingest />,
  },
  {
    showOnList: true,
    url: 'platform-kb',
    slug: 'platform-kb',
    title: 'Platform Knowledge Base',
    name: 'ChatBotDocument',
    category: 'Configuration',
    level: Level.Instance,
    component: <PlatformKB />,
  },
  {
    showOnList: true,
    url: 'system-alert-configuration',
    slug: 'system-alert-configuration',
    title: 'System Alert Configuration',
    name: 'SystemAlertConfiguration',
    category: 'Configuration',
    level: Level.Instance,
    component: <SystemAlertConfiguration />,
  },
  {
    showOnList: true,
    url: 'invalid-password-reset-analytics',
    slug: 'invalid-password-reset-analytics',
    title: 'Invalid Password Reset Email Analytics',
    name: 'InvalidPasswordResetEmailAnalytics',
    category: 'Configuration',
    level: Level.Instance,
    component: <InvalidPasswordResetAnalyticsPage />,
  },
  // {
  //   showOnList: true,
  //   url: 'iso-configuration',
  //   slug: 'iso-configuration',
  //   title: 'ISO Configuration',
  //   name: 'IsoConfiguration',
  //   category: 'Configuration',
  //   level: Level.Instance,
  //   component: <IsoConfiguration />,
  // },
  // {
  //   showOnList: true,
  //   url: 'document-detail',
  //   slug: 'document-detail',
  //   title: 'Document Detail',
  //   name: 'Companies',
  //   category: 'Configuration',
  //   level: Level.Instance,
  //   component: <DocumentDetail />,
  // },
  {
    url: 'disbursement-configuration',
    slug: 'disbursement-configuration',
    title: 'Disbursement Configuration',
    name: 'DisbursementValidation',
    category: 'Configuration',
    level: Level.Instance,
    component: <DisbursementConfig />,
  },
  {
    showOnList: true,
    url: 'transfer-agent-setting',
    slug: 'transfer-agent-setting',
    title: 'Transfer Agent Setting',
    name: 'TAIntegration',
    category: 'Configuration',
    level: Level.Instance,
    component: <TransferAgentSetting />,
  },
  {
    showOnList: true,
    url: 'branding-package-configuration',
    slug: 'branding-package-configuration',
    title: 'Branding Package',
    name: 'BrandingPackage',
    category: 'Configuration',
    level: Level.Company,
    component: <WhiteLabelConfiguration />,
  },
  {
    showOnList: true,
    url: 'money-movement-configuration',
    slug: 'money-movement-configuration',
    title: 'Money Movement Configuration',
    name: 'MoneyMovementEntitlement',
    category: 'Configuration',
    level: Level.Instance,
    component: <MoneyMovementConfig />,
  },
  {
    showOnList: true,
    url: 'company-accounting-policy',
    slug: 'company-accounting-policy',
    title: 'Company Accounting Policy',
    name: 'CompanyAccountingPolicy',
    category: 'Others',
    level: Level.Company,
    component: <CompanyAccountingPolicy />,
  },
];

export const ComponentsNames = [
  {
    url: 'company-profile',
    name: 'Companies',
  },
  {
    url: 'vesting-templates',
    name: 'VestingTemplates',
  },
  {
    url: 'performance-rule',
    name: 'PerformanceRule',
  },
  {
    url: 'trade-pre-clearance',
    name: 'TradePreClearance',
  },
  {
    url: 'trading-black-out-configuration',
    name: 'TradingBlackOut',
  },
  {
    url: 'notification',
    name: 'Notification',
  },
  {
    url: 'users',
    name: 'CustomerUsers',
  },
  {
    url: 'settlement-config',
    name: 'SettlementConfigs',
  },
  {
    url: 'retirement-config',
    name: 'RetirementConfigs',
  },
  {
    url: 'election-window-config',
    name: 'ElectionWindowConfigurations',
  },
  {
    url: 'exercisability-instructions',
    name: 'ExerciseWindowConfiguration',
  },
  {
    url: 'exercisability-tier-configuration',
    name: 'MaturitySchedule',
  },
  {
    url: 'plan-management',
    name: 'Plans',
  },
  {
    url: 'grant-management',
    name: 'Plans',
  },
  {
    url: 'sharepool-management',
    name: 'Plans',
  },
  {
    url: 'roles',
    name: 'CustomerRoles',
  },
  {
    url: 'department',
    name: 'CustomerDepartments',
  },
  {
    url: 'job-title',
    name: 'CustomerTitles',
  },
  {
    url: 'employee-types',
    name: 'CustomerEmployeeTypes',
  },
  {
    url: 'system-approvers',
    name: 'SystemApprovers',
  },
  {
    url: 'grant-management',
    name: 'Grants',
  },
  {
    url: 'grant-list',
    name: 'Grants',
  },
  {
    url: 'plan-approvals',
    name: 'PlanApproval',
  },
  {
    url: 'bank-account',
    name: 'Banks',
  },
  {
    url: 'rsu-awards',
    name: 'RSUAwards',
  },
  {
    url: 'psu-awards',
    name: 'PSUAwards',
  },
  {
    url: 'option-awards',
    name: 'OptionAwards',
  },
  {
    url: 'sar-awards',
    name: 'SarAwards',
  },
  {
    url: 'sar-approvals',
    name: 'SarAwardApproval',
  },
  {
    url: 'sar-exercises',
    name: 'SarExercise',
  },
  {
    url: 'unified-awards',
    name: ['RSUAwards', 'PSUAwards', 'OptionAwards', 'Warrants'],
  },
  {
    url: 'awards-agreement',
    name: 'AwardAgreement',
  },
  {
    url: 'participants',
    name: 'Participants',
  },
  {
    url: 'vendor-broker-terms-and-conditions',
    name: 'VendorTermsAndConditions',
  },
  {
    url: 'company-terms-and-conditions',
    name: 'TermsAndConditions',
  },
  //   {
  //   url: 'terms-and-conditions',
  //   name: 'TermsAndConditions',
  // },
  {
    url: 'user-defined-field',
    name: 'UserDefinedFields',
  },
  {
    url: 'participant-broadcast',
    name: 'ParticipantBroadcastMessages',
  },
  {
    url: 'ppt-access-list',
    name: 'ManagePptAccessList',
  },
  {
    url: 'participant-service',
    name: 'ParticipantService',
  },
  {
    url: 'tax-setup',
    name: 'CompanyTax',
  },
  {
    url: 'tax-period',
    name: 'CompanyTax',
  },
  {
    url: 'reports',
    name: 'DynamicReport',
  },
  {
    url: 'task-management-console',
    name: 'TaskManagerConsole',
  },
  {
    url: 'email-logs',
    name: 'Notification',
  },
  {
    url: 'grant-acceptance-template',
    name: 'Notice',
  },
  {
    url: 'espp',
    name: 'OfferingPeriod',
  },
  {
    url: 'convertible',
    name: 'Convertible',
  },
  {
    url: 'valuation',
    name: 'Valuation',
  },
  {
    url: 'securities',
    name: 'Securities',
  },
  {
    url: '/transactions/exercise',
    name: 'OptionExercise',
  },
  {
    url: '/task/user-approval',
    permissionName: 'CustomerUserApproval',
    name: 'System User Approval',
  },
  {
    url: 'customer-success',
    name: 'Customer Success',
  },
  {
    url: 'role-publish',
    name: 'RolePublishedToCompany',
  },
  {
    url: 'role-mapping',
    name: 'AllowedRoleMapping',
  },
  {
    url: 'customer-list',
    name: 'Company',
  },
  {
    url: 'customer-list/create',
    name: 'Company',
  },
  {
    url: 'brokerage-import-export',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/brokerage-account/list',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/brokerage-account',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/trade-order/list',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/trade-order',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/transfer-and-allocate',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/transfer-and-allocate/pending-shares',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/transfer-and-allocate/details',
    name: 'BrokerageImportExport',
  },
  {
    url: 'transfer-agent/drs-registration',
    name: 'TransferAgentImportExport',
  },
  {
    url: 'transfer-agent/drs-registration/pending-shares',
    name: 'TransferAgentImportExport',
  },
  {
    url: 'transfer-agent/drs-registration/details',
    name: 'TransferAgentImportExport',
  },

  {
    url: 'dashboard/disbursement',
    name: 'BrokerageImportExport',
  },
  {
    url: 'dashboard/disbursement/details',
    name: 'BrokerageImportExport',
  },
  {
    url: 'sandbox',
    name: 'Sandbox',
  },
  {
    url: 'dashboard/brokerage-account',
    name: 'Companies',
  },
  {
    url: 'dashboard/trade-order',
    name: 'Companies',
  },
  {
    url: 'brokerage-account',
    name: 'Companies',
  },
  {
    url: 'trade-order',
    name: 'Companies',
  },
  {
    url: 'commission',
    name: 'Commission',
  },
  {
    url: 'fee',
    name: 'fee',
  },
  {
    url: 'feature-set',
    name: 'FeatureSet',
  },
  {
    url: 'permission-manager',
    name: 'PermissionManager',
  },
  {
    url: 'company-mapping',
    name: 'AllowedCompanyMapping',
  },
  {
    url: 'customer-success-automation',
    name: 'Customer Success',
  },
  {
    url: 'broker-trade-tool',
    name: 'BrokerTradeTool',
  },
  {
    url: 'done-trade',
    name: 'DoneTrade',
  },
  {
    url: 'push-to-production',
    name: 'Production',
  },
  {
    url: 'fmv-configuration',
    name: 'FMVConfiguration',
  },
  {
    url: 'transaction-configuration',
    name: 'Plans',
    // name: 'TransactionConfiguration',
  },
  {
    url: 'transfer-agent-setting',
    name: 'TAIntegration',
  },
  {
    url: 'transfer-agent',
    name: 'TransferAgentImportExport',
  },
  {
    url: 'transfer-agent-import-export',
    name: 'DrsInstruction',
  },

  // {
  //   url: 'transactions/purchase',
  //   name: 'Companies',
  // },
  {
    url: 'job-scheduler',
    name: 'Scheduler',
  },
  {
    url: 'sandbox-configuration',
    name: 'SandboxConfiguration',
  },
  {
    url: 'sso-configuration',
    name: 'SsoConfiguration',
  },
  {
    url: 'export-pdf',
    name: 'HoldingExporter',
  },
  {
    url: 'iso-configuration',
    name: 'IsoConfiguration',
  },
  {
    url: 'branding-package-configuration',
    name: 'BrandingPackage',
  },
  {
    url: 'platform-kb',
    name: 'ChatBotDocument',
  },
  {
    url: 'charter-stock-classes',
    name: 'StockClass',
  },
  {
    url: 'broker-sso-configuration',
    name: 'BrokerSsoConfiguration',
  },
  {
    url: 'broker-attestation-audit-logs',
    name: 'BrokerAttestationAuditLogs',
  },
  {
    url: 'advisor-api-config',
    name: 'ManageAdvisorApiKey',
  },
  {
    url: 'company-accounting-policy',
    name: 'CompanyAccountingPolicy',
  },
  {
    url: 'Valuation',
    name: 'Valuation',
  },
  {
    url: 'valuation',
    name: 'Valuation',
  },
  {
    url: 'summary',
    name: 'CapTableSummary',
  },
  {
    url: 'funding-round',
    name: 'FundingRound',
  },
  {
    url: 'financing-rounds',
    name: 'FinancingRounds',
  },
];

export const MyTaskRouteList = [
  {
    url: '/task/user-approval',
    permissionName: 'CustomerUserApproval',
    name: 'System User Approval',
  },
  {
    url: '/task/participants-approval',
    permissionName: 'ParticipantApproval',
    name: 'Participant Approval',
  },
  {
    url: '/task/rsu-awards-approval',
    permissionName: 'RSUAwardApproval',
    name: 'RSU Awards Approval',
  },
  {
    url: '/task/psu-awards-approval',
    permissionName: 'PSUAwardApproval',
    name: 'PSU Awards Approval',
  },
  {
    url: '/task/plan-approval',
    permissionName: 'PlanApproval',
    name: 'Plan Approval',
  },
  {
    url: '/task/settlement-config-approval',
    permissionName: 'SettlementConfigs',
    name: 'Settlement Config Approval',
  },
  {
    url: '/task/option-awards-approval',
    permissionName: 'OptionAwardApproval',
    name: 'Option Awards Approval',
  },
  {
    url: '/task/pre-clearance-requests',
    permissionName: 'TradePreClearanceApproval',
    name: 'Trade Pre-Clearance Approval',
  },
  {
    url: '/settings/organization-settings/plan-management',
    permissionName: 'Plans',
    name: 'Replenish Share Pool',
  },
  {
    url: '/settings/organization-settings/plan-management',
    permissionName: 'Plans',
    name: 'Plan Expiry',
  },
  {
    url: '/settings/organization-settings/sharepool-management',
    permissionName: 'Plans',
    name: 'Share Pool Management',
  },
  {
    url: '/task/tax-setup-approval',
    permissionName: 'TaxApproval',
    name: 'Tax Approval',
  },
  {
    url: '/task/performance-rule-approval',
    permissionName: 'PerformanceRuleApproval',
    name: 'Performance Rule Approval',
  },
  {
    url: '/settings/organization-settings/performance-rule',
    permissionName: 'PerformanceRule',
    name: 'Multiplier Entry',
  },
  {
    url: '/transactions/exercise',
    permissionName: 'OptionExercise',
    name: 'Exercise pending',
  },
];

export const EquityAwardsList = [
  {
    url: '/awards/rsu-awards',
    permissionName: 'RSUAwards',
    name: 'Restricted Stock',
  },
  {
    url: '/awards/option-awards',
    permissionName: 'OptionAwards',
    name: 'Option Awards',
  },
  {
    url: '/sar-awards',
    permissionName: 'SarAwards',
    name: 'SAR Awards',
  },
  {
    url: '/sar-approvals',
    permissionName: 'SarAwardApproval',
    name: 'SAR Award Approval',
  },
  {
    url: '/sar-exercises',
    permissionName: 'SarExercise',
    name: 'SAR Exercises',
  },
  {
    url: '/awards/psu-awards',
    permissionName: 'PSUAwards',
    name: 'PSU Awards',
  },
  {
    url: '/espp',
    permissionName: 'OfferingPeriod',
    name: 'ESPP',
  },
  {
    url: '/awards/fair-value',
    permissionName: ['RSUAwards', 'OptionAwards', 'PSUAwards'],
    name: 'Fair Value',
  },
  {
    url: '/awards/unified-award-import',
    permissionName: ['RSUAwards', 'OptionAwards', 'PSUAwards'],
    name: 'Unified Award Import',
  },
  {
    url: '/awards/unified-awards',
    permissionName: ['RSUAwards', 'OptionAwards', 'PSUAwards', 'Warrants'],
    name: 'Unified Awards',
  },
  {
    url: '/awards/awards-agreement',
    permissionName: ['RSUAwards', 'OptionAwards', 'PSUAwards', 'Warrants'],
    name: 'Award Agreement',
  },
  {
    url: '/awards/vesting-tranches',
    permissionName: ['RSUAwards', 'OptionAwards', 'PSUAwards'],
    name: 'Vesting Tranches',
  },
];

export const CapTableList = [
  {
    url: '/cap-table/summary',
    permissionName: 'CapTableSummary',
    name: 'Cap Table',
  },
  {
    url: '/cap-table/funding-round',
    permissionName: 'FundingRound',
    name: 'Funding Round',
  },
  {
    url: '/cap-table/exit-waterfall',
    permissionName: 'Waterfall',
    name: 'Exit Waterfall',
  },
  {
    url: '/cap-table/financing-rounds',
    permissionName: 'FundingRound',
    name: 'Financing Rounds',
  },
  {
    url: '/cap-table/securities',
    permissionName: 'Securities',
    name: 'Shares',
  },
  {
    url: '/cap-table/convertibles',
    permissionName: 'Convertible',
    name: 'Convertibles',
  },
  {
    url: '/cap-table/warrants',
    permissionName: 'Warrants',
    name: 'Warrants',
  },
  {
    url: '/cap-table/valuation',
    permissionName: 'Valuation',
    name: 'Valuations',
  },
  {
    url: '/cap-table/unified-instrument-import',
    permissionName: ['Securities', 'Convertible', 'Warrants'],
    name: 'Unified Import',
  },
  {
    url: '/cap-table/ocf-import',
    permissionName: 'OcfImport',
    name: 'OCF Import/Export',
  },
];
export const TransactionsList = [
  {
    url: '/transactions/release',
    permissionName: 'Release',
    name: 'Release',
  },
  {
    url: '/transactions/exercise',
    permissionName: 'OptionExercise',
    name: 'Exercise/Purchase',
  },
  {
    url: '/transactions/warrant-exercises',
    permissionName: 'WarrantExercise',
    name: 'Warrant Exercises',
  },
  {
    url: '/transactions/purchase',
    permissionName: 'Release',
    name: 'Purchase',
  },
  {
    url: '/transactions/transfer-and-allocate',
    permissionName: 'TransferAllocate',
    name: 'Transfer and Allocate',
  },
  {
    url: '/transactions/drs-registration',
    permissionName: 'DrsRegistration',
    name: 'DRS Registration',
  },
  {
    url: '/transactions/external-transaction-import',
    permissionName: 'TransferAllocate',
    name: 'External Transaction Import',
  },
  {
    url: '/transactions/external-tax-import',
    permissionName: 'TransactionTaxImport',
    name: 'External Tax Import',
  },
  {
    url: '/transactions/inbound-share-transfer-import',
    permissionName: 'InboundShareTransfer',
    name: 'Inbound Share Transfer Import',
  },
  {
    url: '/transactions/unified-import-export',
    permissionName: 'StockSplit',
    name: 'Unified Transaction Import',
  },
  {
    url: '/transactions/convertible',
    permissionName: 'Convertible',
    name: 'Convertible',
  },
];

export const TaskManagementConsole = [
  {
    url: 'task-management-console',
    permissionName: 'TaskManagerConsole',
    name: 'Job Console',
  },
];

export const ReportList = [
  {
    url: '/reports',
    permissionName: 'DynamicReport',
    name: 'Reports',
  },
];
